import { useCallback } from "react";

import { useAOIOBOptions } from "./aoiob";

import type { SidebarOptionItem, SidebarRootItem } from "../sidebar.types";

/**
 * Hook that returns a builder for default menu items.
 *
 * Internalizes AOIOB options computation via `useAOIOBOptions`. NAF options
 * are passed in because they're computed once and shared across all default
 * items. Used for menu groups like "Tools" that combine backend-configured
 * sub-items with dynamically-added NAF apps and AOIOB links.
 */
export const useDefaultMenuItem = () => {
  const aoiobOptions = useAOIOBOptions();

  return useCallback(
    (
      menuItem: API.SidebarMenuItem_Resolved,
      nafOptions: SidebarOptionItem[]
    ): SidebarRootItem => {
      const standardOptions: SidebarOptionItem[] = (
        menuItem.subItems || []
      ).map((subItem) => ({
        type: "standard" as const,
        title: subItem.title,
        route: subItem.route,
        icon: subItem.icon,
        newTab: subItem.newTab,
      }));

      const menuOptions: SidebarOptionItem[] = [
        ...standardOptions,
        ...(menuItem.showNAFApps ? nafOptions : []),
        ...(menuItem.showAOIOB ? aoiobOptions : []),
      ].filter(Boolean);

      return {
        ...menuItem,
        menuOptions,
      };
    },
    [aoiobOptions]
  );
};
